import {
  forEach,
  isObject,
  mapKeys,
  mapValues,
  pickBy,
  snakeCase,
} from "lodash/fp";
import { Result, err, ok } from "neverthrow";

import { getHostedFieldSelector } from "@zapier/payment/esm/components/CreditCardForm/HostedFields/utils";
import {
  CreditCardBrandCode,
  HostedFieldAdditionalData,
  HostedFieldProcessorInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
} from "@zapier/payment/esm/types";
import { logToSentry } from "@zapier/payment/esm/utils/logToSentry";
import { StripeSdk } from "@zapier/stripe";

const pickByDeep = (obj: object): object =>
  mapValues(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    val => (isObject(val) ? pickByDeep(val) : val),
    pickBy(Boolean, obj)
  );

const assertNever = (value: never, noThrow?: boolean): never => {
  if (noThrow) {
    return value;
  }

  throw new Error(
    `Unhandled discriminated union member: ${JSON.stringify(value)}`
  );
};

type BespokeResult<T, E = Error> =
  | { ok: true; value: T }
  | { ok: false; error: E };

/** @internal */
export class StripeProcessor implements HostedFieldProcessorInterface {
  stripe: stripe.Stripe;
  elements: stripe.elements.Elements;
  static instance: HostedFieldProcessorInterface | undefined;
  static elementsRegistry: Map<HostedFieldType, any> = new Map();

  constructor(stripe: stripe.Stripe) {
    this.stripe = stripe;
    this.elements = stripe.elements({
      fonts: [
        {
          cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans",
        },
      ],
    });
  }

  async createFields(
    style: any,
    onChange: (b: HostedFieldType, a: HostedFieldValue) => void
  ) {
    if (
      StripeProcessor.elementsRegistry.has("number") &&
      StripeProcessor.elementsRegistry.has("expiry") &&
      StripeProcessor.elementsRegistry.has("cvc")
    ) {
      return;
    }

    const types = ["cardNumber", "cardExpiry", "cardCvc"] as const;
    const simpleTypes = {
      cardNumber: "number",
      cardExpiry: "expiry",
      cardCvc: "cvc",
    } as const;

    forEach(type => {
      const element = this.elements.create(type, {
        style: {
          base: style,
        },
      });

      const simpleType = simpleTypes[type];

      StripeProcessor.elementsRegistry.set(simpleType, element);

      element.addEventListener("change", response => {
        onChange(simpleType, {
          isEmpty: Boolean(response?.empty),
          originalError: response?.error?.message ?? "",
          brand: (response?.brand as CreditCardBrandCode) ?? "unknown",
        });
      });

      element.mount(getHostedFieldSelector(simpleType));
    }, types);
  }

  destroyFields() {
    if (StripeProcessor.elementsRegistry.size === 0) return;

    const types = ["number", "expiry", "cvc"] as const;

    forEach(type => {
      const element = StripeProcessor.elementsRegistry.get(type);

      if (element) {
        element.destroy();
        StripeProcessor.elementsRegistry.delete(type);
      }
    }, types);
  }

  async createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> {
    const firstElement = StripeProcessor.elementsRegistry.values().next().value;
    const cleanData = pickBy(Boolean, additionalData);
    const data = mapKeys(snakeCase, cleanData);
    const { error, token } = await this.stripe.createToken(firstElement, data);

    if (error) {
      throw new Error(error.message);
    }

    return {
      // @ts-ignore - our bespoke token/details types are mismatched from Stripe's
      token: token.id,
      // @ts-ignore - our bespoke token/details types are mismatched from Stripe's
      details: token.card,
    };
  }

  async confirmCardSetupIntent(
    clientSecret: string,
    billingDetails?: stripe.BillingDetails
  ): Promise<BespokeResult<stripe.setupIntents.SetupIntent, stripe.Error>> {
    const response = await this.stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: StripeProcessor.elementsRegistry.get("number"),
        billing_details: billingDetails,
      },
    });

    if (response.error) {
      return { ok: false, error: response.error } as const;
    }

    if (response.setupIntent) {
      return { ok: true, value: response.setupIntent } as const;
    }

    return assertNever(response as never, true);
  }

  static async create(): Promise<Result<HostedFieldProcessorInterface, Error>> {
    if (!StripeProcessor.instance) {
      const stripeSdkResult = await StripeSdk.get();
      if (stripeSdkResult.isOk()) {
        StripeProcessor.instance = new StripeProcessor(stripeSdkResult.value);
      } else {
        logToSentry("StripeProcessor.create", stripeSdkResult.error);
        return err(stripeSdkResult.error);
      }
    }

    return ok(StripeProcessor.instance);
  }
}

export type StripeProcessorType = InstanceType<typeof StripeProcessor>;

export default StripeProcessor.create;
